import { Injectable } from "@angular/core";
import {
  ActiveUserProfilePromiseService,
  CcyPairPromiseLookupService,
  ClientPromiseLookupService,
  CurrencyPairGroupPromiseLookupService,
  BaseTableDefinitionFactory,
  TraderGroupLookupService,
  COLUMN_DESK_ID,
  ColumnFilterType,
  ProfileSettingKeys,
  ProfilePromiseActionService,
  BFXNotificationType,
} from "shared/cubiq-services";
import * as CubiqTS from "shared/cubiq-model-multi-asset";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { BfxColDef } from "shared/cubiq-services/model/TableDefinition";
import { RowNode } from "ag-grid-community";
import { QueryRuleImpl, OrderType } from "shared/cubiq-model-multi-asset";
import { BmarkTraderGroupNameHelper } from "shared/cubiq-services/model/benchmarks/helpers/BmarkTradeGroupNameHelper";
import { BfxBaseBlotterFilteringService } from "app/bfx-base-blotter/bfx-base-blotter-filtering.service";
import { BmarkClientOrdersBlotterMode } from "shared/cubiq-services/model/benchmarks/enums/BmarkClientOrdersBlotterMode.enum";
import { BfxUserProfileService } from "app/bfx-common/services/bfx-user-profile.service";
import { BmarkSummaryViewFilterType } from "../models/bmark-summary-view-filter-type.enum";
import {
  ACTIVE_BM_VIEW_FILTER,
  FILLED_BM_VIEW_FILTER,
} from "../models/bmark-summary-view-filter.const";
import { BfxBlotterFilterService } from "app/bfx-blotter-filter/bfx-blotter-filter.service";
import { BfxBlotterFilter } from "app/bfx-blotter-filter/bfx-blotter-filter.constants";
import { BmarkBlotterViewFilterSelectionService } from "./bmark-blotter-view-filter-selection.service";
import { Subscription } from "rxjs";

export class BmarkAllocationHelperModel {
  public allocationId: CubiqTS.Long; // primary
  public orderId: string; // reference
  public allocationType: CubiqTS.AllocationType;
  public baseCcy: string;
  public nonBaseCcy: string;
  public ccyPair: string;

  constructor(allocationId: CubiqTS.Long) {
    this.allocationId = allocationId;
  }
}

@Injectable()
export class BmarkBlotterBaseFilteringService extends BfxBaseBlotterFilteringService {
  private readonly profileKey = `${ProfileSettingKeys.userProfileDefaultBlotter}${
    CubiqTS.BlotterType[CubiqTS.BlotterType.ORDER]
  }/BMARK/SERVICE`;
  protected _filterConfiguration = [];
  protected _viewFilterConfiguration = null;

  protected _viewFilterType = BmarkSummaryViewFilterType.ALL;
  protected _viewFilterSelectionSubscription: Subscription;
  protected get viewFilterType() {
    return this._viewFilterType;
  }

  protected set viewFilterType(type) {
    this._viewFilterType = type;
    this._viewFilterConfiguration = [
      ...(BmarkSummaryViewFilterType.ACTIVE === type ? [this.activeFilter] : []),
      ...(BmarkSummaryViewFilterType.FILLED === type ? [this.filledFilter] : []),
    ];
  }

  protected get viewFilterConfiguration() {
    if (this._viewFilterConfiguration == null) {
      this.viewFilterType = this._viewFilterType;
    }

    return this._viewFilterConfiguration;
  }

  protected myBmarkTradeGroupNameHelper: any;
  protected readonly activeFilter;
  protected readonly filledFilter;

  constructor(
    protected tableDefinitionFactory: BaseTableDefinitionFactory,
    protected activeUserService: ActiveUserProfilePromiseService,
    protected clientService: ClientPromiseLookupService,
    protected currencyPairService: CcyPairPromiseLookupService,
    protected currencyPairGroupService: CurrencyPairGroupPromiseLookupService,
    protected traderGroupLookup: TraderGroupLookupService,
    protected profileActionService: ProfilePromiseActionService,
    protected userProfileService: BfxUserProfileService,
    protected viewFilterService: BmarkBlotterViewFilterSelectionService,
  ) {
    super(
      tableDefinitionFactory,
      activeUserService,
      clientService,
      currencyPairService,
      currencyPairGroupService,
    );
    this.myBmarkTradeGroupNameHelper = new BmarkTraderGroupNameHelper(
      this.traderGroupLookup,
      COLUMN_DESK_ID,
    );
    const systemUserMap = new Map([["-1", "System"]]);
    this.activeFilter = BfxBlotterFilterService.transform(ACTIVE_BM_VIEW_FILTER, systemUserMap);
    this.filledFilter = BfxBlotterFilterService.transform(FILLED_BM_VIEW_FILTER, systemUserMap);

    this._viewFilterSelectionSubscription = this.viewFilterService.selection$.subscribe((type) => {
      this.viewFilterType = type;
    });
  }

  public hasViewFilter(): boolean {
    return (
      BmarkSummaryViewFilterType.ACTIVE === this.viewFilterType ||
      BmarkSummaryViewFilterType.FILLED === this.viewFilterType
    );
  }

  public doFiltersPass(
    rowData: RowNode | CubiqTS.OrderData,
    filters: Array<BfxBlotterFilter>,
    // consider refactoring a typed opt object
    omitRuleForNotifications = false,
    dealtMode?: boolean,
    embeddedTableMode?: boolean,
  ): boolean {
    if (embeddedTableMode !== true && this.hasViewFilter()) {
      let viewFilterConfiguration = this.viewFilterConfiguration ?? [];

      if (
        !super.doFiltersPass(rowData, viewFilterConfiguration, omitRuleForNotifications, dealtMode)
      ) {
        return false;
      }
    }

    if (filters == null || (Array.isArray(filters) && filters.length == 0)) {
      return true;
    }

    return super.doFiltersPass(rowData, filters, omitRuleForNotifications, dealtMode);
  }

  public updateFilterSelection(filter: Array<string>): void {
    let filterDataJson = "[]";
    this._filterConfiguration = filter;
    let profileSettings: Array<CubiqTS.ProfileSettingImpl> = [];
    if (Array.isArray(filter)) {
      filterDataJson = JSON.stringify(filter);
    }
    profileSettings.push(
      this.userProfileService.createProfileSettingObject(this.profileKey, filterDataJson),
    );

    this.profileActionService.updateProfile(profileSettings, false).then(
      () => {},
      (reason) => {
        logger.out.warn(`${this.constructor.name}.updateFilterSelection() failed ${reason}`);
      },
    );
  }

  public getFilterConfigurationFromProfile() {
    return this._filterConfiguration;
  }

  // overwrite of base method only for benchmark filtering
  protected preprocessFilteredValue(
    column: BfxColDef,
    rowData: RowNode | CubiqTS.OrderData | any,
    rule: QueryRuleImpl,
    dealtMode?: boolean,
    omitRuleForNotifications = false,
  ): string {
    let result: any;
    // this if is a workaround for BMARK Deal notifications filtering
    if (
      rowData != null &&
      (rowData.notifType === BFXNotificationType.BMARK_FILLED ||
        rowData.notifType === BFXNotificationType.BMARK_DEAL_FAILURE) &&
      rowData.hasOwnProperty("tradeNotifData")
    ) {
      result = rowData.tradeNotifData[column.field];
    } else {
      let orderData: CubiqTS.OrderData = rowData instanceof RowNode ? rowData.data : rowData;
      if (column.columnFilterType === ColumnFilterType.TRADERGROUP) {
        if (orderData.orderType === OrderType.BENCHMARK) {
          result = (
            this.myBmarkTradeGroupNameHelper as BmarkTraderGroupNameHelper
          ).getTraderGroupIds(rowData);
          return result.length > 1 ? result.map((elem) => elem.toString()) : result.toString();
        }
      } else if (
        column.columnFilterType === ColumnFilterType.CURRENCY_PAIR ||
        column.columnFilterType === ColumnFilterType.CURRENCY_PAIR_GROUP
      ) {
        return this.handleCurrencyPairFiltering(rowData, omitRuleForNotifications);
      } else if (column.columnFilterType === ColumnFilterType.ORDERSTATE) {
        return this.getOrderStateValue(rowData, column);
      } else {
        result = super.preprocessFilteredValue(column, rowData, rule, dealtMode);
      }

      // if (result instanceof InstrumentColumnData) {
      // 	return result.instrument;
      // }
    }
    return result;
  }

  /** HELPER METHODS */

  /**
   * Function which is extended to return locked state in orders.
   * For now it returns only `Modify In Pending`, but can be extended with others
   *
   * @param rowDataObj
   * @param column
   */
  protected getOrderStateValue(rowDataObj: RowNode | CubiqTS.OrderData, column: BfxColDef): string {
    let valueFromObject =
      rowDataObj instanceof RowNode ? rowDataObj.data[column.field] : rowDataObj[column.field];
    let orderStateValue = CubiqTS.OrderState[valueFromObject];
    return orderStateValue === CubiqTS.OrderState[CubiqTS.OrderState.ACCEPTANCE_NEEDED] &&
      valueFromObject["orderId"] != null
      ? "ACCEPTANCE_NEEDED_MODIFY_LOCK"
      : orderStateValue;
  }

  /**
   * Method which extracts and returns
   * all of the Ccy Pair Ids from the Order Data object
   * including the Allocations in an array,
   * except when it's invoked from the notifications.
   * @param rowData
   * @param omitRuleForNotifications - flag to return the baseCcyPair
   */
  private handleCurrencyPairFiltering(rowData: any | RowNode, omitRuleForNotifications = false) {
    let result: any = [];
    let value = rowData instanceof RowNode ? rowData.data : rowData;
    let orderData: CubiqTS.OrderDataImpl = value != null ? value : null;
    let rootCcyPair = orderData.isInvertedOrderFg
      ? orderData.nonBaseCode + "/" + orderData.baseCode
      : orderData.baseCode + "/" + orderData.nonBaseCode;
    if (omitRuleForNotifications) {
      return rootCcyPair;
    }
    let allocationData: CubiqTS.AllocationDataImpl =
      orderData.allocationData != null ? orderData.allocationData[0] : null;
    let blotterMode = rowData instanceof RowNode ? rowData.data.blotterMode : rowData.blotterMode;
    let ccyPairMetaMap: Map<string, BmarkAllocationHelperModel> = new Map<
      string,
      BmarkAllocationHelperModel
    >();
    if (blotterMode !== BmarkClientOrdersBlotterMode.RISK) {
      let rootCcyPairHelperModel: BmarkAllocationHelperModel = new BmarkAllocationHelperModel(
        orderData.orderId,
      );
      rootCcyPairHelperModel.ccyPair = rootCcyPair;
      ccyPairMetaMap.set(orderData.orderId.toString(), rootCcyPairHelperModel);
    }
    this.processAllocation(allocationData, ccyPairMetaMap);
    for (let [key, value] of ccyPairMetaMap.entries()) {
      let ccyPairName = value.ccyPair;
      result.push(ccyPairName);
    }

    return result.length === 0 ? [] : result;
  }

  /**
   * Copied method from the getTraderGroupName function in the
   * TraderGroup helper service only modified to get the Ccy Pairs
   * from the allocations and component allocations in OrderData object.
   *
   * @param allocationData
   * @param allocationMetaMap
   */
  private processAllocation(
    allocationData: CubiqTS.AllocationDataImpl,
    allocationMetaMap: Map<string, BmarkAllocationHelperModel>,
  ) {
    if (allocationData instanceof CubiqTS.AllocationDataImpl) {
      let baseCcy = typeof allocationData.baseCcy === "string" ? allocationData.baseCcy : null;
      let nonBaseCcy =
        typeof allocationData.nonbaseCcy === "string" ? allocationData.nonbaseCcy : null;
      if (
        typeof baseCcy === "string" &&
        !allocationMetaMap.has(allocationData.allocationId?.toString())
      ) {
        let helperModel: BmarkAllocationHelperModel = new BmarkAllocationHelperModel(
          allocationData.allocationId,
        );
        helperModel.baseCcy = baseCcy;
        helperModel.nonBaseCcy = nonBaseCcy;
        helperModel.allocationType = allocationData.allocationType;
        helperModel.ccyPair = baseCcy + "/" + nonBaseCcy;
        allocationMetaMap.set(allocationData.allocationId?.toString(), helperModel);
      }

      // component allocations
      if (
        Array.isArray(allocationData.componentAllocations) &&
        allocationData.componentAllocations.length > 0 &&
        allocationData.componentAllocations[0] instanceof CubiqTS.AllocationDataImpl
      ) {
        if (allocationData.componentAllocations.length > 0) {
          for (let allocationItem of allocationData.componentAllocations) {
            this.processAllocation(allocationItem, allocationMetaMap);
          }
        }
      }
    }
  }
}
